/* Comparatorul de produse — căsuța „Compară” de pe card, bara de jos și pagina de comparație.
 *
 * Design: docs/superpowers/specs/2026-10-17-comparator-produse-design.md (cu review Codex).
 * Cerut de Tudor pe 17.10.2026: „bifezi un check mark și ți se adaugă produsul jos”.
 *
 * Ce s-a învățat din încercarea din 26.07 (scoasă din temă, AM26-011/012) și e cerință aici:
 *  - bara apare DOAR pe colecție, căutare și pagina de comparație — scriptul se încarcă
 *    numai acolo (snippets/am-compara-init.liquid), nu „te urmărește” pe PDP, coș, 404;
 *  - pe telefon e o pastilă de ≤ 64 px, desfăcută doar la cerere;
 *  - bula de chat urcă deasupra barei (`--am-compara-h`), nu se suprapune.
 *
 * Starea: localStorage['am_compara_v1'] = {fam, items:[{id, handle, title, img}]}, per
 * domeniu (.ro și .bg au liste separate, ca orice localStorage). Limita de 4 și regula „o
 * singură familie” se impun în `reduce`, nu doar prin căsuțe dezactivate.
 *
 * Rândurile paginii: grupurile de filtre din am-typesense-filters.js (`window.AmSpecsConfig`),
 * aceeași sursă de etichete ro/bg ca filtrele. Valorile vin din
 * `/products/<handle>?view=am-compara` (templates/product.am-compara.liquid), în contextul pieței.
 *
 * Nicio dată personală în evenimente: acțiunea, familia (tipul produsului) și numărul.
 */
(function () {
  'use strict';

  var KEY = 'am_compara_v1';
  var MAX = 4;

  /* Familia comparației = tipul produsului. Excepția: boxele pasive și cele active au
     același grup de fațete și se compară firesc între ele (pasiv + amplificator vs activ).
     `product.type` e TRADUS pe .bg, deci lista conține și numele bulgărești, plus forma
     românească rămasă netradusă pe unele produse .bg (măsurat 17.10 pe products.json .bg). */
  var FAM_BOXE = {
    'Boxe pasive': 1, 'Boxe amplificate': 1,
    'Пасивни високоговорители': 1, 'Активни високоговорители': 1
  };
  function famOf(type) {
    var t = String(type || '').trim();
    return FAM_BOXE[t] ? 'boxe' : t;
  }

  /* Familia CANONICĂ pentru GA4 (`compara_tip`): o listă închisă, aceeași în pixel și în proxy
     (review Codex 17.10: textul liber al tipului nu e protecție pentru date personale, iar pe
     .bg tipul e tradus, deci rapoartele s-ar fi rupt pe limbi). Numele bulgărești sunt
     perechile reale ro↔bg din products.json pe ambele domenii (4.413 produse, 17.10). Orice tip
     necunoscut devine `alt`, deci evenimentul nu se pierde. */
  var TIP_CANONIC = {
    'Amplificator': 'amplificator', 'Интегрирани усилватели': 'amplificator',
    'Amplificator de putere': 'amplificator_putere', 'Крайни стъпала': 'amplificator_putere',
    'Preamplificator': 'preamplificator', 'Preamplificator Phono': 'preamplificator',
    'Предусилватели': 'preamplificator', 'Грамофонни предусилватели': 'preamplificator',
    'Receiver': 'receiver', 'Procesor AV': 'receiver', 'Procesor surround': 'receiver',
    'АВ ресийвъри': 'receiver', 'АВ процесори': 'receiver',
    'Boxe pasive': 'boxe', 'Boxe amplificate': 'boxe',
    'Пасивни високоговорители': 'boxe', 'Активни високоговорители': 'boxe',
    'Subwoofer': 'subwoofer', 'Субуфер': 'subwoofer',
    'Soundbar': 'soundbar', 'Саундбар': 'soundbar',
    'Casti': 'casti', 'Слушалки': 'casti',
    'Amplificator casti': 'amplificator_casti', 'DAC casti': 'amplificator_casti',
    'Усилватели за слушалки': 'amplificator_casti', 'DAC за слушалки': 'amplificator_casti',
    'DAC': 'dac', 'Цифрови конвертори (DAC)': 'dac',
    'Streamer': 'streamer', 'Mediaplayer': 'streamer', 'Стриймъри': 'streamer', 'Мултимедийни плеъри': 'streamer',
    'Pick-up': 'pickup', 'Грамофони': 'pickup',
    'Doza pick-up': 'doza', 'Грамофонни дози': 'doza',
    'CD Players & Recorders': 'cd', 'CD плейъри и записващи устройства': 'cd',
    'Televizor': 'televizor', 'Телевизори': 'televizor',
    'Videoproiector': 'proiector', 'Проектори': 'proiector',
    'Cabluri': 'cablu', 'Кабели': 'cablu',
    'Stativ boxe': 'stativ', 'Стойки за високоговорители': 'stativ',
    'Pachete promo': 'pachet', 'Промо пакети': 'pachet',
    'Filtru audio': 'alimentare', 'Sursa de curent cu filtrare': 'alimentare', 'Filtru': 'alimentare',
    'Оптимизатори захранване': 'alimentare', 'Филтри': 'alimentare',
    'Refurbished': 'resigilat', 'Разопаковани продукти': 'resigilat'
  };
  function famKey(type) {
    var t = String(type || '').trim();
    if (!t) return '';
    return Object.prototype.hasOwnProperty.call(TIP_CANONIC, t) ? TIP_CANONIC[t] : 'alt';
  }

  function emptyState() { return { fam: '', famLabel: '', items: [] }; }

  function sanitize(s) {
    if (!s || typeof s !== 'object' || !Array.isArray(s.items)) return emptyState();
    var seen = {};
    var items = s.items.filter(function (it) {
      if (!it || !it.id || !isHandle(it.handle) || seen[it.id]) return false;
      seen[it.id] = 1;
      return true;
    }).slice(0, MAX);
    return items.length
      ? { fam: String(s.fam || ''), famLabel: String(s.famLabel || s.fam || ''), items: items }
      : emptyState();
  }

  /* Reducerul: singurul loc care schimbă starea. Întoarce {state, err}; `err` lasă
     starea neschimbată. */
  function reduce(state, action) {
    var s = sanitize(state);
    var a = action || {};
    if (a.type === 'clear') return { state: emptyState(), err: '' };
    if (a.type === 'remove') {
      var rest = s.items.filter(function (it) { return String(it.id) !== String(a.id); });
      return { state: rest.length ? { fam: s.fam, famLabel: s.famLabel, items: rest } : emptyState(), err: '' };
    }
    if (a.type === 'add' || a.type === 'replace') {
      var it = a.item || {};
      if (!it.id || !isHandle(it.handle)) return { state: s, err: 'invalid' };
      var fam = famOf(a.fam);
      var famLabel = String(a.famLabel || a.fam || '');
      if (a.type === 'replace') return { state: { fam: fam, famLabel: famLabel, items: [it] }, err: '' };
      if (s.items.some(function (x) { return String(x.id) === String(it.id); })) return { state: s, err: '' };
      if (s.items.length && s.fam !== fam) return { state: s, err: 'familie' };
      if (s.items.length >= MAX) return { state: s, err: 'limita' };
      return { state: { fam: fam, famLabel: famLabel, items: s.items.concat([it]) }, err: '' };
    }
    return { state: s, err: '' };
  }

  function isHandle(h) { return typeof h === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(h); }

  function parseHandles(param) {
    var out = [];
    String(param || '').split(',').forEach(function (h) {
      h = h.trim().toLowerCase();
      if (isHandle(h) && out.indexOf(h) < 0 && out.length < MAX) out.push(h);
    });
    return out;
  }

  /* Ce grup de fațete descrie comparația: cel care acoperă cele mai multe chei `specs.*`
     prezente efectiv pe produse. NU după numele tipului: 9 tipuri au alt nume decât cheia
     din CONFIGS (`Pick-up`/`Pickup`, `DAC`/`Dac`, `Videoproiector`/`Proiector`…, măsurat
     17.10), iar pe .bg tipul e tradus. La egalitate câștigă prima cheie din CONFIGS. */
  function pickConfig(configs, products) {
    var present = {};
    products.forEach(function (p) {
      Object.keys((p && p.specs) || {}).forEach(function (k) { present[k] = 1; });
    });
    var best = null, bestN = 0;
    Object.keys(configs || {}).forEach(function (name) {
      var n = 0;
      (configs[name] || []).forEach(function (g) {
        var key = String(g.field).replace(/^specs_/, '');
        var ex = EXACT[g.field];
        if (present[key] || (ex && ex.keys.some(function (k) { return present[k]; }))) n++;
      });
      if (n > bestN) { best = name; bestN = n; }
    });
    return best;
  }

  /* Valoarea exactă bate intervalul de filtru acolo unde există (boxele au mm, dB, Ω și W
     exacte, măsurat 17.10 pe KEF: `spk_inaltime_mm` 303 față de `spk_inaltime_bucket`
     „25-33 cm”). Cheia de interval rămâne rezerva când lipsește valoarea exactă. */
  var EXACT = {
    specs_spk_inaltime_bucket: { keys: ['spk_inaltime_mm'], unit: 'mm' },
    specs_spk_latime_bucket: { keys: ['spk_latime_mm'], unit: 'mm' },
    specs_spk_adancime_bucket: { keys: ['spk_adancime_mm'], unit: 'mm' },
    specs_spk_sensitivity_bucket: { keys: ['sensitivity_db'], unit: 'dB' },
    specs_spk_impedance_display: { keys: ['impedance_ohm'], unit: 'Ω' },
    specs_spk_putere_admisa_bucket: { keys: ['rec_amp_min_w', 'rec_amp_max_w'], unit: 'W', range: true }
  };
  /* Rânduri în afara filtrelor, adăugate la final când măcar un produs are valoarea. */
  var EXTRA = [
    { key: 'weight_kg', unit: 'kg', label: 'row_weight' },
    { key: 'bass_hz', unit: 'Hz', label: 'row_bass' },
    { key: 'max_spl_db', unit: 'dB', label: 'row_spl' }
  ];

  function isEmpty(v) {
    return v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length);
  }

  /* Celula: {text, norm}. `norm` e forma de comparat — listele ca MULȚIMI (ordinea nu
     contează), numerele ca numere. */
  function cell(v, unit, fmtNum, valueLabel) {
    if (isEmpty(v)) return { text: '', norm: '' };
    if (Array.isArray(v)) {
      var labels = v.map(function (x) { return valueLabel(String(x)); });
      return { text: labels.join(', '), norm: v.map(String).sort().join('|') };
    }
    if (typeof v === 'number') return { text: fmtNum(v) + (unit ? ' ' + unit : ''), norm: String(v) };
    if (typeof v === 'boolean') v = String(v);
    return { text: valueLabel(String(v)), norm: String(v) };
  }

  function buildRows(groups, products, opts) {
    var fmtNum = opts.fmtNum, valueLabel = opts.valueLabel, groupTitle = opts.groupTitle;
    var rows = [], used = {};
    (groups || []).forEach(function (g) {
      var key = String(g.field).replace(/^specs_/, '');
      var ex = EXACT[g.field];
      var cells = products.map(function (p) {
        var sp = (p && p.specs) || {};
        if (ex) {
          if (ex.range) {
            var a = sp[ex.keys[0]], b = sp[ex.keys[1]];
            if (!isEmpty(a) && !isEmpty(b)) {
              return { text: fmtNum(a) + '–' + fmtNum(b) + ' ' + ex.unit, norm: a + '-' + b };
            }
          } else if (!isEmpty(sp[ex.keys[0]])) {
            return cell(sp[ex.keys[0]], ex.unit, fmtNum, valueLabel);
          }
        }
        return cell(sp[key], '', fmtNum, valueLabel);
      });
      used[key] = 1;
      if (ex) ex.keys.forEach(function (k) { used[k] = 1; });
      pushRow(rows, groupTitle(g.field, g.label), cells);
    });
    EXTRA.forEach(function (x) {
      if (used[x.key]) return;
      var cells = products.map(function (p) { return cell(((p && p.specs) || {})[x.key], x.unit, fmtNum, valueLabel); });
      pushRow(rows, opts.t(x.label), cells);
    });
    return rows;
  }

  function pushRow(rows, label, cells) {
    if (cells.every(function (c) { return c.norm === ''; })) return;
    var first = cells[0].norm;
    var differs = cells.some(function (c) { return c.norm !== first; });
    rows.push({ label: label, cells: cells, differs: differs });
  }

  var core = {
    KEY: KEY, MAX: MAX, famOf: famOf, famKey: famKey, reduce: reduce, sanitize: sanitize,
    parseHandles: parseHandles, pickConfig: pickConfig, buildRows: buildRows, isHandle: isHandle
  };
  if (typeof window !== 'undefined') window.AmComparaCore = core;
  if (typeof document === 'undefined' || window.__amCompara) return;
  window.__amCompara = true;

  /* ─── DOM ──────────────────────────────────────────────────────────────────── */

  var I18N = {};
  try {
    var src = document.getElementById('am-compara-i18n');
    I18N = src ? JSON.parse(src.textContent) : {};
  } catch (e) { I18N = {}; }
  function t(k, vars) {
    var s = I18N[k] || k;
    Object.keys(vars || {}).forEach(function (v) { s = s.split('{{ ' + v + ' }}').join(vars[v]); });
    return s;
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  var LANG = String(document.documentElement.lang || 'ro').slice(0, 2).toLowerCase();
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function load() {
    try { return sanitize(JSON.parse(localStorage.getItem(KEY) || 'null')); } catch (e) { return emptyState(); }
  }
  function save(s) {
    try { localStorage.setItem(KEY, JSON.stringify(s)); } catch (e) { /* mod privat: starea trăiește doar în pagină */ }
  }
  var state = load();

  /* Acțiunile sunt o listă închisă: adaugat, scos, golit, deschis, diferente.
     `n` = câte produse are comparația DUPĂ acțiune (review Codex 17.10: înainte era „înainte”
     la scoatere și „după” la adăugare). `tip` = familia canonică; la scoatere/golire se ia
     ÎNAINTE de schimbare (`tipCurent()`), altfel golirea ar raporta o familie goală. Pe pagina de
     comparație `n` și `tip` vin din pagină, nu din starea locală (link partajat = stare goală). */
  function tipCurent() { return famKey(state.famLabel || state.fam); }
  function publish(actiune, extra) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        var d = Object.assign({ actiune: actiune, tip: tipCurent(), n: state.items.length }, extra || {});
        if (!d.tip) delete d.tip;
        Shopify.analytics.publish('audio_monkey:compara', d);
      }
    } catch (e) { /* măsurarea nu are voie să strice comparatorul */ }
  }

  function compareUrl(items) {
    return ROOT + 'search?view=compara&p=' + items.map(function (i) { return i.handle; }).join(',');
  }

  /* ─── Bara de jos ──────────────────────────────────────────────────────────── */

  var isPage = !!document.querySelector('[data-am-compara-page]');
  var bar = null, pending = null;

  function ensureBar() {
    if (bar || isPage) return bar;
    bar = document.createElement('div');
    bar.className = 'am-compara-bar';
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', t('bar_label'));
    bar.hidden = true;
    bar.innerHTML =
      '<div class="am-compara-bar__in">' +
        '<button type="button" class="am-compara-bar__toggle" aria-expanded="false"></button>' +
        '<ul class="am-compara-bar__slots"></ul>' +
        '<div class="am-compara-bar__act">' +
          '<a class="am-compara-bar__go" href="#"></a>' +
          '<button type="button" class="am-compara-bar__clear">' + esc(t('clear')) + '</button>' +
        '</div>' +
      '</div>' +
      '<div class="am-compara-bar__msg" role="status" aria-live="polite"></div>';
    document.body.appendChild(bar);
    bar.addEventListener('click', onBarClick);
    if (window.ResizeObserver) new ResizeObserver(setHeightVar).observe(bar);
    return bar;
  }

  function setHeightVar() {
    var h = bar && !bar.hidden ? bar.offsetHeight : 0;
    document.documentElement.style.setProperty('--am-compara-h', h + 'px');
  }

  function renderBar() {
    if (isPage) return;
    var n = state.items.length;
    if (!n && !bar) return;
    ensureBar();
    bar.hidden = n === 0;
    document.documentElement.classList.toggle('am-compara-on', n > 0);
    if (!n) { setHeightVar(); return; }
    var slots = '';
    for (var i = 0; i < MAX; i++) {
      var it = state.items[i];
      slots += it
        ? '<li class="am-compara-bar__slot">' +
            (it.img ? '<img src="' + esc(it.img) + '" alt="" width="44" height="44" loading="lazy">' : '') +
            '<span class="am-compara-bar__name">' + esc(it.title) + '</span>' +
            '<button type="button" class="am-compara-bar__x" data-am-x="' + esc(it.id) + '" aria-label="' +
              esc(t('remove', { title: it.title })) + '">×</button>' +
          '</li>'
        : '<li class="am-compara-bar__slot is-empty" aria-hidden="true"></li>';
    }
    bar.querySelector('.am-compara-bar__slots').innerHTML = slots;
    var toggle = bar.querySelector('.am-compara-bar__toggle');
    toggle.textContent = n === 1 ? t('count_one') : t('count_other', { count: n });
    var go = bar.querySelector('.am-compara-bar__go');
    go.textContent = t('go') + ' (' + n + ')';
    go.href = compareUrl(state.items);
    go.classList.toggle('is-off', n < 2);
    go.setAttribute('aria-disabled', n < 2 ? 'true' : 'false');
    setHeightVar();
  }

  /* Mesajele simple dispar după 5 s, ca bara pliată de pe telefon să rămână ≤ 64 px
     (AM26-012); cele cu butoane (conflictul de familie) rămân până la răspuns și sunt
     singurele vizibile cu bara pliată (CSS: `.has-actions`). */
  var sayTimer = 0;
  function say(text, actions) {
    if (!bar) return;
    var m = bar.querySelector('.am-compara-bar__msg');
    clearTimeout(sayTimer);
    m.innerHTML = esc(text) + (actions || '');
    m.classList.toggle('has-actions', !!actions);
    if (text && !actions) sayTimer = setTimeout(function () { m.innerHTML = ''; m.classList.remove('has-actions'); setHeightVar(); }, 5000);
    setHeightVar();
  }

  function onBarClick(e) {
    var x = e.target.closest('[data-am-x]');
    if (x) {
      var gone = state.items.filter(function (i) { return String(i.id) === x.getAttribute('data-am-x'); })[0];
      var tip0 = tipCurent();
      apply({ type: 'remove', id: x.getAttribute('data-am-x') });
      publish('scos', { tip: tip0 });
      if (gone) say(t('removed', { title: gone.title }));
      return;
    }
    if (e.target.closest('.am-compara-bar__clear')) {
      var tipG = tipCurent();
      apply({ type: 'clear' });
      publish('golit', { tip: tipG });
      return;
    }
    if (e.target.closest('.am-compara-bar__toggle')) {
      var open = bar.classList.toggle('is-open');
      e.target.closest('.am-compara-bar__toggle').setAttribute('aria-expanded', open ? 'true' : 'false');
      setHeightVar();
      return;
    }
    var go = e.target.closest('.am-compara-bar__go');
    if (go && state.items.length < 2) { e.preventDefault(); say(t('need_two')); return; }
    if (go) return; /* `deschis` se publică o singură dată, la încărcarea paginii de comparație */
    if (e.target.closest('[data-am-da]') && pending) {
      apply({ type: 'replace', item: pending.item, fam: pending.fam, famLabel: pending.famLabel });
      say(t('added', { title: pending.item.title }));
      pending = null;
      publish('adaugat');
      return;
    }
    if (e.target.closest('[data-am-nu]')) { pending = null; say(''); syncChecks(); }
  }

  function apply(action) {
    var r = reduce(state, action);
    state = r.state;
    if (!r.err) save(state);
    renderBar();
    syncChecks();
    return r.err;
  }

  /* ─── Căsuțele de pe carduri ───────────────────────────────────────────────── */

  function syncChecks() {
    var ids = {};
    state.items.forEach(function (i) { ids[String(i.id)] = 1; });
    var full = state.items.length >= MAX;
    document.querySelectorAll('input[data-am-compara]').forEach(function (inp) {
      var on = !!ids[inp.getAttribute('data-am-compara')];
      inp.checked = on;
      inp.disabled = full && !on;
    });
  }

  document.addEventListener('change', function (e) {
    var inp = e.target;
    if (!inp || !inp.matches || !inp.matches('input[data-am-compara]')) return;
    var item = {
      id: inp.getAttribute('data-am-compara'),
      handle: inp.getAttribute('data-handle'),
      title: inp.getAttribute('data-title') || '',
      img: inp.getAttribute('data-img') || ''
    };
    var fam = inp.getAttribute('data-fam');
    var famLabel = inp.getAttribute('data-fam-label') || fam;
    ensureBar();
    if (!inp.checked) {
      var tipS = tipCurent();
      apply({ type: 'remove', id: item.id });
      publish('scos', { tip: tipS });
      say(t('removed', { title: item.title }));
      return;
    }
    var err = apply({ type: 'add', item: item, fam: fam, famLabel: famLabel });
    if (err === 'limita') { inp.checked = false; say(t('limit')); return; }
    if (err === 'familie') {
      inp.checked = false;
      pending = { item: item, fam: fam, famLabel: famLabel };
      say(t('conflict', { categorie: state.famLabel || state.fam }),
        ' <button type="button" class="am-compara-bar__yes" data-am-da>' + esc(t('conflict_yes')) + '</button>' +
        ' <button type="button" class="am-compara-bar__no" data-am-nu>' + esc(t('conflict_no')) + '</button>');
      return;
    }
    if (!err) { say(t('added', { title: item.title })); publish('adaugat'); }
  });

  /* Filtrele (Typesense și cele ale temei) și paginarea înlocuiesc cardurile prin AJAX.
     Observatorul ascultă doar pe <main> și re-aplică starea o dată per rafală (debounce). */
  var timer = 0;
  var main = document.getElementById('content') || document.querySelector('main');
  if (main && window.MutationObserver) {
    new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(syncChecks, 120);
    }).observe(main, { childList: true, subtree: true });
  }
  window.addEventListener('storage', function (e) {
    if (e.key !== KEY) return;
    state = load();
    renderBar();
    syncChecks();
  });

  document.documentElement.classList.add('am-compara-ready');
  renderBar();
  syncChecks();

  /* ─── Pagina de comparație (/search?view=compara&p=…) ─────────────────────── */

  if (!isPage) return;
  var page = document.querySelector('[data-am-compara-page]');
  /* „Înapoi la produse” duce la lista de unde a venit clientul, dacă e pe același site;
     altfel rămâne linkul din Liquid (toate produsele). */
  var back = page.querySelector('[data-am-inapoi]');
  try {
    var ref = document.referrer ? new URL(document.referrer) : null;
    if (back && ref && ref.origin === location.origin && !/view=compara/.test(ref.search)) back.href = ref.pathname + ref.search;
  } catch (e) { /* referrer invalid: rămâne linkul implicit */ }
  var out = page.querySelector('[data-am-compara-out]');
  var params = new URLSearchParams(location.search);
  var handles = parseHandles(params.get('p'));
  if (!handles.length && state.items.length) {
    handles = state.items.map(function (i) { return i.handle; });
    try { history.replaceState(null, '', compareUrl(state.items)); } catch (e) { /* fără URL partajabil, pagina merge oricum */ }
  }
  if (!handles.length) { out.innerHTML = '<p class="am-compara-empty">' + esc(t('empty')) + '</p>'; return; }

  var pageTip = '';
  out.innerHTML = '<p class="am-compara-loading">' + esc(t('loading')) + '</p>';
  var nf;
  try { nf = new Intl.NumberFormat(LANG === 'bg' ? 'bg-BG' : 'ro-RO', { maximumFractionDigits: 2 }); } catch (e) { nf = null; }
  function fmtNum(n) { return nf ? nf.format(n) : String(n); }

  Promise.all(handles.map(function (h) {
    return fetch(ROOT + 'products/' + encodeURIComponent(h) + '?view=am-compara', { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .catch(function () { return { handle: h, missing: true }; });
  })).then(function (products) {
    /* `tip` și `n` din pagină, nu din starea locală: un link partajat se deschide fără stare. */
    pageTip = famKey(renderPage(products));
    publish('deschis', { n: handles.length, tip: pageTip });
  }).catch(function () {
    out.innerHTML = '<p class="am-compara-empty">' + esc(t('error')) + '</p>';
  });

  function renderPage(products) {
    var cfg = window.AmSpecsConfig || null;
    /* Un URL editat sau partajat poate amesteca categorii (căsuțele nu lasă, URL-ul da).
       Familia comparației e a primului produs valid; celelalte devin coloane cu mesaj. */
    var fam0 = '', famLabel0 = '';
    products.forEach(function (p) {
      if (p.missing || p.hidden) return;
      if (!fam0) { fam0 = famOf(p.type); famLabel0 = p.type || ''; }
      else if (famOf(p.type) !== fam0) p.otherFam = true;
    });
    var ok = products.filter(function (p) { return !p.missing && !p.hidden && !p.otherFam; });
    var groups = [];
    if (cfg) {
      var name = pickConfig(cfg.CONFIGS, ok);
      groups = name ? cfg.CONFIGS[name] : [];
    }
    var valueLabel = cfg ? function (v) { return cfg.valueLabel(v, LANG); } : function (v) { return v; };
    var groupTitle = cfg ? function (f, l) { return cfg.groupTitle(f, l, LANG); } : function (f, l) { return l; };
    var rows = buildRows(groups, products.map(function (p) { return (p.missing || p.hidden || p.otherFam) ? {} : p; }), {
      fmtNum: fmtNum, valueLabel: valueLabel, groupTitle: groupTitle, t: t
    });
    var brand = products.map(function (p) { var v = p.otherFam ? '' : (p.vendor || ''); return { text: v, norm: v }; });
    if (brand.some(function (c) { return c.norm; })) {
      rows.unshift({ label: t('row_brand'), cells: brand, differs: brand.some(function (c) { return c.norm !== brand[0].norm; }) });
    }
    var anyDiff = rows.some(function (r) { return r.differs; });

    var head = '<tr><td class="am-compara-corner"></td>' + products.map(function (p) {
      var x = '<button type="button" class="am-compara-col__x" data-am-scoate="' + esc(p.handle) + '" aria-label="' +
        esc(t('remove', { title: p.title || p.handle })) + '">×</button>';
      if (p.missing || p.hidden) {
        return '<th scope="col" class="am-compara-col is-missing">' + x + '<p>' + esc(t('not_available')) + '</p></th>';
      }
      if (p.otherFam) {
        return '<th scope="col" class="am-compara-col is-missing">' + x + '<p>' + esc(p.title || '') + '</p><p>' + esc(t('other_family')) + '</p></th>';
      }
      return '<th scope="col" class="am-compara-col">' + x + p.head + '</th>';
    }).join('') + '</tr>';

    var body = rows.map(function (r) {
      return '<tr' + (r.differs ? ' class="is-diff"' : '') + '><th scope="row">' + esc(r.label) + '</th>' +
        r.cells.map(function (c) { return '<td>' + (c.text ? esc(c.text) : '<span aria-label="—">—</span>') + '</td>'; }).join('') +
        '</tr>';
    }).join('');

    var sheets = products.filter(function (p) { return p.sheet && !p.otherFam; });
    var sheetHtml = sheets.length
      ? '<details class="am-compara-sheet"><summary>' + esc(t('full_sheet')) + '</summary><div class="am-compara-sheet__cols" style="--am-n:' + products.length + '">' +
          products.map(function (p) {
            return '<div class="am-compara-sheet__col"><p class="am-compara-sheet__title">' + esc(p.title || '') + '</p>' + (p.otherFam ? '' : (p.sheet || '')) + '</div>';
          }).join('') + '</div></details>'
      : '';

    out.innerHTML =
      (rows.length && anyDiff && products.length > 1
        ? '<label class="am-compara-diff"><input type="checkbox" data-am-diff checked> ' + esc(t('only_diff')) + '</label>' : '') +
      '<div class="am-compara-scroll" tabindex="0" role="region" aria-label="' + esc(t('page_title')) + '">' +
        '<table class="am-compara-table' + (anyDiff && products.length > 1 ? ' only-diff' : '') + '" style="--am-n:' + products.length + '">' +
          '<thead>' + head + '</thead><tbody>' + body + '</tbody></table>' +
      '</div>' +
      (rows.length ? '' : '<p class="am-compara-empty">' + esc(t('no_specs')) + '</p>') +
      sheetHtml;
    return famLabel0;
  }

  page.addEventListener('change', function (e) {
    if (!e.target.matches('[data-am-diff]')) return;
    var tbl = page.querySelector('.am-compara-table');
    if (tbl) tbl.classList.toggle('only-diff', e.target.checked);
    publish('diferente', { pornit: e.target.checked ? 1 : 0, n: handles.length, tip: pageTip });
  });
  page.addEventListener('click', function (e) {
    var x = e.target.closest('[data-am-scoate]');
    if (!x) return;
    var h = x.getAttribute('data-am-scoate');
    var it = state.items.filter(function (i) { return i.handle === h; })[0];
    if (it) apply({ type: 'remove', id: it.id });
    publish('scos', { n: handles.length - 1, tip: pageTip });
    var left = handles.filter(function (k) { return k !== h; });
    location.href = left.length ? ROOT + 'search?view=compara&p=' + left.join(',') : ROOT + 'search?view=compara';
  });
})();
